import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { formatPence, STATUS_LABEL, statusTone, type Entry } from "@/lib/bank-reconciliation";

const TONE = {
  ok: "bg-green-100 text-green-800", warn: "bg-amber-100 text-amber-800",
  bad: "bg-red-100 text-red-700", info: "bg-sky-100 text-sky-800",
};
export function StatusPill({ status }: { status: string }) {
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${TONE[statusTone(status)]}`}>{STATUS_LABEL[status] ?? status}</span>;
}

export function EntryTable({ title, entries, selected, onToggle, onToggleAll, selectable, testid }: {
  title: string; entries: Entry[]; selected: Set<number>; onToggle: (id: number) => void;
  onToggleAll: (ids: number[], on: boolean) => void; selectable: boolean; testid: string;
}) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [search,setSearch] = useState("");
  const [unmatchedOnly,setUnmatchedOnly] = useState(false);
  const [page,setPage] = useState(0);
  const filtered = entries.filter(e=>(!unmatchedOnly || !e.status.startsWith("reconciled_")) &&
    `${e.day} ${e.description} ${e.reference ?? ""} ${e.channel}`.toLowerCase().includes(search.toLowerCase()));
  const pageCount = Math.max(1,Math.ceil(filtered.length/100));
  const currentPage = Math.min(page,pageCount-1);
  const visible = filtered.slice(currentPage*100,(currentPage+1)*100);
  const total = entries.reduce((a, e) => a + e.amount, 0);
  const allOn = visible.length > 0 && visible.every((e) => selected.has(e.id));
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          {selectable && (
            <input type="checkbox" checked={allOn} aria-label={`Select visible ${title}`}
              onChange={(e) => onToggleAll(visible.map((x) => x.id), e.target.checked)} data-testid={`check-all-${testid}`} />
          )}
          <h3 className="text-sm font-bold">{title}</h3>
          <span className="text-xs text-duffells-grey/60">{entries.length} entries</span>
        </div>
        <span className="text-xs font-mono font-semibold">{formatPence(total)}</span>
      </div>
      <div className="p-2 border-b flex flex-wrap gap-2 items-center text-xs">
        <input aria-label={`Search ${title}`} placeholder="Search reference, description or date" value={search}
          onChange={e=>{setSearch(e.target.value);setPage(0);}} className="rounded border px-2 py-1 flex-1 min-w-40" />
        <label className="flex items-center gap-1"><input type="checkbox" checked={unmatchedOnly}
          onChange={e=>{setUnmatchedOnly(e.target.checked);setPage(0);}} />Unmatched only</label>
        <span>{filtered.length} shown · page {currentPage+1} / {pageCount}</span>
        <button type="button" disabled={currentPage===0} onClick={()=>setPage(currentPage-1)} className="disabled:opacity-40" aria-label={`Previous ${title} page`}>Previous</button>
        <button type="button" disabled={currentPage+1>=pageCount} onClick={()=>setPage(currentPage+1)} className="disabled:opacity-40" aria-label={`Next ${title} page`}>Next</button>
      </div>
      {entries.length === 0 ? (
        <p className="p-6 text-center text-sm text-duffells-grey/50">No entries on this side.</p>
      ) : (
        <div className="divide-y divide-slate-100 max-h-[480px] overflow-auto">
          {!visible.length && <p className="p-4 text-xs text-center">No entries match these filters.</p>}
          {visible.map((e) => {
            const isOpen = open.has(e.id);
            return (
              <div key={e.id} className={selected.has(e.id) ? "bg-orange-50" : ""} data-testid={`entry-${e.id}`}>
                <div className="grid grid-cols-[20px_20px_1fr_auto] gap-2 px-3 py-2 items-start text-xs">
                  {selectable ? (
                    <input type="checkbox" checked={selected.has(e.id)} onChange={() => onToggle(e.id)}
                      aria-label={`Select entry ${e.id}`} data-testid={`check-entry-${e.id}`} className="mt-0.5" />
                  ) : <span />}
                  <button type="button" onClick={() => setOpen((p) => { const n = new Set(p); if (n.has(e.id)) n.delete(e.id); else n.add(e.id); return n; })}
                    aria-label="Toggle evidence" data-testid={`toggle-evidence-${e.id}`}>
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                  <div className="min-w-0">
                    <div className="font-medium truncate" title={e.description}>{e.description}</div>
                    <div className="text-duffells-grey/60 flex flex-wrap gap-x-3">
                      <span>{e.day}</span>
                      {!e.status.startsWith("reconciled_") && <span>{e.ageDays} days old</span>}
                      {e.reference && <span className="font-mono">ref {e.reference}</span>}
                      <span className="uppercase">{e.channel}</span>
                      {e.decisionId != null && <span>decision #{e.decisionId}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-semibold">{formatPence(e.amount)}</div>
                    <StatusPill status={e.status} />
                  </div>
                </div>
                {isOpen && (
                  <div className="px-3 pb-3 pl-14 text-[11px] space-y-1">
                    <div><span className="font-semibold">Rule:</span> {e.rule ?? "none"}</div>
                    <pre className="bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap break-words max-h-48 overflow-auto">
                      {JSON.stringify(e.evidence ?? null, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
